import { useEffect, useState } from 'react'
import { TAG_COLORS_QUERY } from 'app/components/tags/graphql'
import { dedupeTagsByName } from 'app/components/tags/tagUtils'
import { gqlQuery } from 'app/lib/graphqlClient'
import { CONTACT_TAGS_FIELD, type FieldGroup } from 'app/lib/workflows/dataContext'
import {
  useChannelMappingStore,
  useLocationStore,
  usePipelineStore,
  useProviderStore,
  useUserStore,
} from 'app/stores/StoreContext'
import { legacyRoleRank } from 'app/types/roles'
import type { Option } from 'app/lib/workflows/actionCatalogs'
import { useUser, useWorkspace } from 'app/utils'
import { defaultChannels } from 'lib/channelDefaults'
import { isSMCOrg } from 'lib/isSMCOrg'
import { logError } from 'lib/logging'
import { getContactBuiltInTags } from 'lib/getBuiltInTags'
import {
  ACTIVITY_TYPE_OPTIONS,
  APPOINTMENT_STATUS_OPTIONS,
  FIELD_VALUE_OPTIONS,
  TASK_STATUS_OPTIONS,
} from 'app/lib/workflows/fieldValueOptions'
import { STAGE_KEY_SEPARATOR } from 'app/lib/workflows/serialize'
import type { NodeErrors } from 'app/lib/workflows/validation'
import { isCompanionPipeline } from 'lib/companionPipelines'
import type { Choice, ChoiceGroup } from './fields'

/** Every inspector panel edits one node: a patch function and the node's validation errors. */
export type SetFn = (patch: Record<string, unknown>) => void
export type Panel<D> = { data: D; set: SetFn; errors: NodeErrors }

/** Field groups → dropdown groups (plain field keys as values), so pickers show Contact / Activity sections. */
export const fieldGroupChoices = (groups: FieldGroup[]): ChoiceGroup[] =>
  groups.map((group) => ({
    label: group.label,
    choices: group.fields.map((field) => ({ value: field.key, label: field.label })),
  }))

/** Value pickers for every fixed-vocabulary condition field, mirroring the contacts table's filter
 *  catalog: static enums (lead status, lifecycle, patient/appointment status, message direction…)
 *  from FIELD_VALUE_OPTIONS, plus record-backed lists (pipeline stages, locations) from the stores.
 *  A fixed-value field must never be free text — a typo against a stored enum is a silent
 *  never-match. Shared by the trigger's entry conditions and the Filter/Branch editors. `status`
 *  belongs to two objects with different vocabularies; the in-scope fields disambiguate. */
/** Display labels for condition values (pipeline stages, locations, statuses). Exported for the
 *  activation preflight, which renders the same enrollment sentence outside the inspector. */
const SMC_CHANNELS = [`Digital Marketing`, `Traditional Marketing`, `Referral Marketing`]

/** SMC sets its three channels by hand; every other org's come from its source/medium mappings. */
export function useChannelChoices(): Choice[] {
  const orgId = useUser()?.orgId
  const smc = !!orgId && isSMCOrg(orgId)
  const channelMappingStore = useChannelMappingStore()
  const [mapped, setMapped] = useState<string[]>([])
  useEffect(() => {
    if (smc) return
    let alive = true
    channelMappingStore.fetchList().then(() => {
      if (alive) setMapped(channelMappingStore.hits.map((mapping) => mapping.channel).filter(Boolean))
    })
    return () => {
      alive = false
    }
  }, [channelMappingStore, smc])
  const channels = smc ? SMC_CHANNELS : [...new Set([...defaultChannels, ...mapped])].sort()
  return channels.map((channel) => ({ value: channel, label: channel }))
}

export function useConditionValueOptions(availableFields: string[]): Record<string, Choice[]> {
  const pipelineStore = usePipelineStore()
  const locationStore = useLocationStore()
  const workspaceId = useWorkspace()?.id
  const [dynamic, setDynamic] = useState<Record<string, Choice[]>>({})
  const [contactTags, setContactTags] = useState<Choice[]>([])
  const providerStore = useProviderStore()
  const userStore = useUserStore()
  const [people, setPeople] = useState<Record<string, Choice[]>>({})
  const channel = useChannelChoices()
  // Appointments store the PMS provider id (`vendorId`), not the record id, and locations can share one.
  useEffect(() => {
    let alive = true
    Promise.all([providerStore.fetchList(), userStore.fetchList()]).then(() => {
      if (!alive) return
      const providerNames = new Map<string, string[]>()
      for (const provider of providerStore.hits) {
        if (!provider.vendorId || provider.deactivated) continue
        const names = providerNames.get(provider.vendorId) ?? []
        if (!names.includes(provider.displayName)) names.push(provider.displayName)
        providerNames.set(provider.vendorId, names)
      }
      setPeople({
        providerId: [...providerNames].map(([value, names]) => ({ value, label: names.join(` / `) })),
        ownerId: userStore.hits
          .filter((user) => !user.archived && !!user.name)
          .map((user) => ({ value: user.id, label: user.name as string })),
      })
    })
    return () => {
      alive = false
    }
  }, [providerStore, userStore])
  useEffect(() => {
    let alive = true
    Promise.all([pipelineStore.fetchList(), locationStore.fetchList()]).then(() => {
      if (!alive) return
      // One entry per pipeline+stage, not per stage name. The value carries the pipeline id because
      // that is what the engine matches on (`pipelines.key`), and enrolling on a stage is impossible
      // without it (TRK-5495). Two pipelines that both have a "Closed" stage are also different
      // things, which the old deduped list quietly merged.
      const stages = pipelineStore.hits
        .filter((pipeline) => !isCompanionPipeline(pipeline))
        .flatMap((pipeline) =>
          (pipeline.stages ?? []).map((stage) => ({
            value: `${pipeline.id}${STAGE_KEY_SEPARATOR}${stage.name}`,
            label: `${pipeline.name} · ${stage.name}`,
          })),
        )
      const locations = locationStore.hits
        .filter((location) => !!location.name)
        .map((location) => ({ value: location.id, label: location.name as string }))
      setDynamic({
        pipelineStage: stages,
        locationId: locations,
        treatmentCategories: [
          { value: `Diagnostic`, label: `Diagnostic` },
          { value: `Preventative`, label: `Preventative` },
          { value: `Restorative`, label: `Restorative` },
          { value: `Endodontics`, label: `Endodontics` },
          { value: `Periodontics`, label: `Periodontics` },
          { value: `Prosthodontics Removable`, label: `Prosthodontics Removable` },
          { value: `Maxillofacial Prosthetics`, label: `Maxillofacial Prosthetics` },
          { value: `Implant Services`, label: `Implant Services` },
          { value: `Prosthodontics Fixed`, label: `Prosthodontics Fixed` },
          { value: `Oral & Maxillofacial Surgery`, label: `Oral & Maxillofacial Surgery` },
          { value: `Orthodontics`, label: `Orthodontics` },
          { value: `Adjunctive General Services`, label: `Adjunctive General Services` },
        ],
      })
    })
    return () => {
      alive = false
    }
  }, [pipelineStore, locationStore])
  // The same list the contact Add-tag popover offers: this workspace's tags, the org-wide ones, and the
  // built-ins that exist only in code.
  useEffect(() => {
    let alive = true
    const scope = workspaceId ? ` AND (workspaceId:${workspaceId} OR isOrgTag:true)` : ``
    gqlQuery(TAG_COLORS_QUERY, { search: `entity:Contact${scope}` }, { requestPolicy: `network-only` }).then(
      (result) => {
        if (!alive) return
        if (result.error) logError(`Failed to fetch contact tags for workflow conditions`, result.error)
        const builtIns = getContactBuiltInTags().map((name) => ({ name }))
        const tags = dedupeTagsByName([...(result.data?.tagColors?.hits ?? []), ...builtIns])
        setContactTags(tags.map((tag) => ({ value: tag.name, label: tag.name })))
      },
    )
    return () => {
      alive = false
    }
  }, [workspaceId])
  const status = availableFields.includes(`dueDate`)
    ? TASK_STATUS_OPTIONS
    : availableFields.includes(`date`)
      ? APPOINTMENT_STATUS_OPTIONS
      : undefined
  return {
    ...FIELD_VALUE_OPTIONS,
    ...(status ? { status } : {}),
    ...(availableFields.includes(`vendor`) ? { type: ACTIVITY_TYPE_OPTIONS } : {}),
    channel,
    ...people,
    ...dynamic,
    ...(contactTags.length ? { [CONTACT_TAGS_FIELD]: contactTags } : {}),
  }
}

/** Reads back as a sentence — "1 day", "3 days" — same rule as the date-trigger moments editor. */
export const waitUnitChoices = (amount: number): Choice[] =>
  [`minute`, `hour`, `day`].map((unit) => ({ value: `${unit}s`, label: amount === 1 ? unit : `${unit}s` }))

// Compact duration number. NB: TextField wraps the <input> in a `div.relative.w-full`, so the width
// has to be constrained on a wrapping box (below), not the input — otherwise that full-width wrapper
// stretches the flex row and pushes the unit away. Right-aligned + spinners hidden → clean "[N] [unit]".
export const DURATION_INPUT = `w-full text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`

export const optionChoices = (options: Option[]): Choice[] =>
  options.map((option) => ({ value: option.id, label: option.name }))

/** Real users (from UserStore) as picker options, prepended with any run-time tokens (e.g. the
 *  contact's owner). Uses local state so a plain (non-observer) panel re-renders when they load.
 *  `workspaceOnly` keeps only workspace-tier users — tier is the numeric role (1–3 = workspace), and
 *  workspace users always carry a workspaceId, never `super` (see docs/role-based-auth.md; the role
 *  check also excludes org users whose records wrongly carry a workspaceId — real in dev data).
 *  Notifications only surface for workspace users, so recipient pickers must not offer staff who can
 *  never see them (execution-audit finding F1). */
export function useUserOptions({
  tokens = [],
  workspaceOnly = false,
}: { tokens?: Option[]; workspaceOnly?: boolean } = {}): Option[] {
  const userStore = useUserStore()
  const [users, setUsers] = useState<Option[]>([])
  useEffect(() => {
    let alive = true
    userStore.fetchList().then(() => {
      if (alive) {
        setUsers(
          userStore.hits
            .filter((user) => !user.archived && !!user.name)
            .filter(
              (user) =>
                !workspaceOnly ||
                (user.type !== `super` &&
                  !!user.workspaceId &&
                  typeof user.role === `number` &&
                  user.role <= legacyRoleRank.workspaceOwner),
            )
            .map((user) => ({ id: user.id, name: user.name as string })),
        )
      }
    })
    return () => {
      alive = false
    }
  }, [userStore, workspaceOnly])
  return [...tokens, ...users]
}
